import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { BarChart2, TrendingUp } from "lucide-react";

// ─── Data builders ────────────────────────────────────────────────────────────

function buildData(programs, scopedSubIds) {
  return programs.map((p) => {
    const subs = scopedSubIds
      ? p.subKegiatan.filter((s) => scopedSubIds.has(s.id))
      : p.subKegiatan;
    if (subs.length === 0)
      return { kode: p.kode, name: p.kode, nama: p.nama, percent: 0, total: 0, done: 0 };
    const done = subs.filter((s) => s.status === "SELESAI").length;
    const percent = Math.round((done / subs.length) * 100);
    return { kode: p.kode, name: p.kode, nama: p.nama, percent, total: subs.length, done };
  });
}

// Kelompokkan sub-kegiatan berdasarkan bulan targetSelesai
function buildMonthlyData(programs, scopedSubIds) {
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Ags", "Sep", "Okt", "Nov", "Des"];
  const map = {};
  programs.forEach((p) => {
    const subs = scopedSubIds
      ? p.subKegiatan.filter((s) => scopedSubIds.has(s.id))
      : p.subKegiatan;
    subs.forEach((s) => {
      if (!s.targetSelesai) return;
      const d = new Date(s.targetSelesai);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth()).padStart(2, "0")}`;
      if (!map[key])
        map[key] = { month: MONTHS[d.getMonth()], selesai: 0, total: 0, sortKey: key };
      map[key].total++;
      if (s.status === "SELESAI") map[key].selesai++;
    });
  });
  return Object.values(map)
    .sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    .slice(-8);
}

function barColor(percent) {
  if (percent >= 80) return "#10b981";
  if (percent >= 50) return "#3b82f6";
  if (percent >= 25) return "#f59e0b";
  return "#6d28d9";
}

// ─── Tooltips ─────────────────────────────────────────────────────────────────

function BarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 text-[11.5px] shadow-md">
      <div className="font-mono text-[10px] text-slate-400">{d.kode}</div>
      <div className="font-semibold text-slate-800 max-w-[220px] leading-snug">{d.nama}</div>
      <div className="mt-1 flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: barColor(d.percent) }} />
        <span className="text-slate-600">
          Selesai <b>{d.done}</b>/{d.total} — <b>{d.percent}%</b>
        </span>
      </div>
    </div>
  );
}

function LineTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-gray-100 bg-white px-3 py-2 text-[11.5px] shadow-md">
      <div className="font-semibold text-slate-800 mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: p.stroke }} />
          <span className="text-slate-600">
            {p.name}: <b>{p.value}</b>
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function ProgramCapaianChart({ programs, scopedSubIds }) {
  const barData = buildData(programs, scopedSubIds);
  const lineData = buildMonthlyData(programs, scopedSubIds);

  const LEGEND = [
    { color: "#10b981", label: "≥80%" },
    { color: "#3b82f6", label: "≥50%" },
    { color: "#f59e0b", label: "≥25%" },
    { color: "#6d28d9", label: "<25%" },
  ];

  return (
    <div
      data-testid="chart-program-capaian"
      className="rounded-2xl border border-violet-100/60 p-5 shadow-sm print:shadow-none relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #f5f3ff 0%, #ffffff 55%)" }}
    >
      {/* Accent stripe */}
      <div
        className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl"
        style={{ background: "linear-gradient(90deg, #7c3aed 0%, #4f46e5 100%)" }}
      />

      {/* Card header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-xl text-white shadow-sm"
            style={{ background: "linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)" }}
          >
            <BarChart2 className="h-5 w-5" strokeWidth={2} />
          </div>
          <div>
            <h3 className="font-display text-base font-bold text-slate-900 leading-tight">
              Analisis Program Pengawasan
            </h3>
            <p className="text-[11px] text-slate-400 mt-0.5">
              Capaian &amp; tren penyelesaian sub-kegiatan — P01 s/d P16
            </p>
          </div>
        </div>
        <span className="text-[11px] font-semibold text-violet-600 bg-violet-50 px-2.5 py-1 rounded-full">
          16 program
        </span>
      </div>

      {/* Two charts side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── Left: Bar chart — Capaian per Program ── */}
        <div
          className="rounded-xl p-4 border border-violet-100/40"
          style={{ background: "linear-gradient(135deg, #faf5ff 0%, #ffffff 100%)" }}
        >
          {/* Panel header */}
          <div className="flex items-center gap-2 mb-3">
            <div
              className="h-1.5 w-5 rounded-full"
              style={{ background: "linear-gradient(90deg, #7c3aed, #4f46e5)" }}
            />
            <span className="text-[12px] font-bold text-slate-700">Capaian per Program</span>
          </div>

          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart
                data={barData}
                margin={{ top: 4, right: 8, left: -22, bottom: 28 }}
              >
                <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#f1f5f9" />
                <XAxis
                  dataKey="name"
                  tick={{ fill: "#475569", fontSize: 9, fontFamily: "monospace" }}
                  tickLine={false}
                  axisLine={false}
                  angle={-45}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  tick={{ fill: "#94a3b8", fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  domain={[0, 100]}
                  unit="%"
                  tickCount={6}
                />
                <Tooltip content={<BarTooltip />} cursor={{ fill: "rgba(124,58,237,0.06)" }} />
                <Bar dataKey="percent" radius={[5, 5, 0, 0]} barSize={14} maxBarSize={18}>
                  {barData.map((d) => (
                    <Cell key={d.kode} fill={barColor(d.percent)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Mini legend */}
          <div className="flex items-center gap-3 flex-wrap mt-2">
            {LEGEND.map((l) => (
              <span key={l.label} className="flex items-center gap-1 text-[10px] text-slate-500">
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* ── Right: Line chart — Trend Penyelesaian ── */}
        <div
          className="rounded-xl p-4 border border-emerald-100/40"
          style={{ background: "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)" }}
        >
          {/* Panel header */}
          <div className="flex items-center gap-2 mb-3">
            <div
              className="h-1.5 w-5 rounded-full"
              style={{ background: "linear-gradient(90deg, #10b981, #059669)" }}
            />
            <span className="text-[12px] font-bold text-slate-700">Trend Penyelesaian</span>
          </div>

          <div style={{ width: "100%", height: 260 }}>
            {lineData.length > 0 ? (
              <ResponsiveContainer>
                <LineChart
                  data={lineData}
                  margin={{ top: 8, right: 12, left: -22, bottom: 4 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="2 4" stroke="#f1f5f9" />
                  <XAxis
                    dataKey="month"
                    tick={{ fill: "#475569", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fill: "#94a3b8", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    allowDecimals={false}
                    tickCount={5}
                  />
                  <Tooltip content={<LineTooltip />} />
                  {/* Total — dashed grey */}
                  <Line
                    type="monotone"
                    dataKey="total"
                    name="Total Deadline"
                    stroke="#cbd5e1"
                    strokeWidth={1.5}
                    strokeDasharray="5 3"
                    dot={false}
                  />
                  {/* Selesai — solid green */}
                  <Line
                    type="monotone"
                    dataKey="selesai"
                    name="Selesai"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    dot={{ r: 4, fill: "#10b981", stroke: "#ffffff", strokeWidth: 2 }}
                    activeDot={{ r: 6, fill: "#059669" }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
                <TrendingUp className="h-8 w-8 text-emerald-200" strokeWidth={1.5} />
                <p className="text-[12px] italic text-slate-400">
                  Data akan muncul saat sub-kegiatan memiliki target selesai
                </p>
              </div>
            )}
          </div>

          {/* Mini legend */}
          <div className="flex items-center gap-4 mt-2">
            <span className="flex items-center gap-1.5 text-[10px] text-slate-500">
              <span className="inline-block h-0.5 w-5 rounded-full bg-emerald-400" />
              Selesai
            </span>
            <span className="flex items-center gap-1.5 text-[10px] text-slate-400">
              <span className="inline-block h-0.5 w-5 rounded-sm border-t-2 border-dashed border-slate-300" />
              Total Deadline
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}
